import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { SOCIAL_LINKS } from "@/common/links";
import { DigitLogo } from "@/landing/components/DigitLogo";
import { PlatformLanguageSwitcher } from "@/landing/components/PlatformLanguageSwitcher";
import {
  hasLocalizedPlatforms,
  useLandingLocale,
  useSiteCopy,
} from "@/landing/lib/site-copy";
import {
  AGENT_PAGES,
  PLATFORM_PAGES,
  USE_CASE_PAGES,
} from "@/landing/lib/site-links";

const LINK_CLASS =
  "text-[14px] text-(--g-ink-2) no-underline transition-colors hover:text-(--g-ink)";

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="m-0 mb-4 text-[13px] text-(--g-ink-3)">{title}</p>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">{children}</ul>
    </div>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
      >
        {children}
      </a>
    </li>
  );
}

export function CliFooter() {
  const copy = useSiteCopy();
  const locale = useLandingLocale();
  const localPlatforms = locale !== "en" && hasLocalizedPlatforms(locale);
  return (
    <footer className="border-t border-(--g-rule) bg-(--g-card) px-(--g-margin) pt-16 pb-10 text-(--g-ink)">
      <div className="grid grid-cols-2 gap-12 md:grid-cols-[minmax(0,1.4fr)_repeat(5,minmax(0,1fr))]">
        <div className="col-span-2 md:col-span-1">
          <DigitLogo variant="nav" light />
          <p className="mt-4 mb-0 max-w-[18rem] text-[14px] leading-[1.5] text-(--g-ink-2)">
            {copy.tagline}
          </p>
        </div>

        <Column title={copy.agents}>
          {AGENT_PAGES.map((link) => (
            <li key={link.slug}>
              <Link
                to="/feature/$slug"
                params={{ slug: link.slug }}
                className={LINK_CLASS}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </Column>

        <Column title={copy.platforms}>
          {PLATFORM_PAGES.map((link) => (
            <li key={link.slug}>
              {localPlatforms ? (
                <Link
                  to="/$locale/platform/$slug"
                  params={{ locale, slug: link.slug }}
                  className={LINK_CLASS}
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  to="/platform/$slug"
                  params={{ slug: link.slug }}
                  className={LINK_CLASS}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </Column>

        <Column title={copy.useCases}>
          {USE_CASE_PAGES.map((link) => (
            <li key={link.slug}>
              <Link
                to="/feature/$slug"
                params={{ slug: link.slug }}
                className={LINK_CLASS}
              >
                {copy[link.copyKey]}
              </Link>
            </li>
          ))}
        </Column>

        <Column title={copy.resources}>
          <li>
            {locale === "en" ? (
              <Link
                to="/blog"
                search={{ page: 1, category: undefined, tag: undefined }}
                className={LINK_CLASS}
              >
                {copy.blog}
              </Link>
            ) : (
              <Link
                to="/$locale/blog"
                params={{ locale }}
                search={{ page: 1, category: undefined, tag: undefined }}
                className={LINK_CLASS}
              >
                {copy.blog}
              </Link>
            )}
          </li>
          <li>
            <Link to="/docs" className={LINK_CLASS}>
              {copy.docs}
            </Link>
          </li>
          <li>
            <Link to="/affiliates" className={LINK_CLASS}>
              {copy.affiliates}
            </Link>
          </li>
        </Column>

        <Column title={copy.company}>
          <External href={SOCIAL_LINKS.twitter}>X</External>
          <External href={SOCIAL_LINKS.linkedin}>LinkedIn</External>
          <External href={SOCIAL_LINKS.github}>GitHub</External>
          <li>
            <Link to="/terms" className={LINK_CLASS}>
              {copy.terms}
            </Link>
          </li>
          <li>
            <Link to="/privacy" className={LINK_CLASS}>
              {copy.privacy}
            </Link>
          </li>
          <li>
            <Link to="/acceptable-use" className={LINK_CLASS}>
              {copy.acceptableUse}
            </Link>
          </li>
        </Column>
      </div>

      <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-(--g-rule-soft) pt-6">
        <p className="m-0 text-[12.5px] text-(--g-ink-3)">
          &copy; {new Date().getFullYear()} Degaus Technologies Ltd
        </p>
        <PlatformLanguageSwitcher />
      </div>
    </footer>
  );
}
